// broker components/DonutStatusChart.js
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { PROJECT_STATUSES, STATUS_TONE } from "../utils/projectStatus";
ChartJS.register(ArcElement, Tooltip, Legend);

const TIME_RANGES = [
  { key: "30d", label: "30 Days" },
  { key: "60d", label: "60 Days" },
  { key: "90d", label: "90 Days" },
  { key: "All Time", label: "All Time" },
];

// light + mid gray tokens (no Tailwind config required)
const GRAY_ACTIVE_BG = "#E9EAEC";  // light fill so it "pops"
const GRAY_ACTIVE_BR = "#E9EAEC";  // was C5C5C5
const GRAY_INACTIVE_BG = "rgba(255,255,255,0.60)";
const GRAY_INACTIVE_BR = "#E6E6E6"; // clear outline for inactive
const GRAY_INACTIVE_HOVER_BG = "#F6F7F8";
const GRAY_INACTIVE_HOVER_BR = "#F6F7F8";//wasD8D8D8

export default function DonutStatusChart({ counts, activeRange, onRangeChange }) {
  const labels = PROJECT_STATUSES;
  const values = labels.map((label) => counts[label] ?? 0);
  const colors = labels.map((label) => STATUS_TONE[label] || "rgba(0,0,0,0.25)");

  const chartData = {
    labels,
    datasets: [{ data: values, backgroundColor: colors, borderWidth: 0, hoverOffset: 10 }],
  };

  const options = {
    plugins: { legend: { display: false } },
    cutout: "70%",
    responsive: true,
    maintainAspectRatio: false,
  };

  return (
    <div className="w-full mx-auto h-full flex flex-col justify-between">
      {/* Header: title stacks above tabs on mobile */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">Project by Status</h2>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {TIME_RANGES.map((r) => {
            const isActive = activeRange === r.key;
            //responsive lable: short on mobile, full on sm+
            const label =
              typeof window !== "undefined" && window.innerWidth < 640
                ? r.key === "all"
                  ? "All"
                  : r.label.replace(" Days", "d")
                : r.label;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => onRangeChange?.(r.key)}
                aria-pressed={isActive}
                className={`px-3 py-1.5 text-sm rounded-md transition focus-visible:outline-none 
                            focus-visible:ring-2 focus-visible:ring-[rgba(211,34,42,0.35)]
                            shadow-[inset_0_0_0_1px_${isActive ? GRAY_ACTIVE_BR : GRAY_INACTIVE_BR}]`}
                style={{
                  backgroundColor: isActive ? GRAY_ACTIVE_BG : GRAY_INACTIVE_BG,
                  // give non-active a clear border + subtle hover on desktop
                  boxShadow: isActive
                    ? `inset 0 0 0 1px ${GRAY_ACTIVE_BR}`
                    : `inset 0 0 0 1px ${GRAY_INACTIVE_BR}`,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = GRAY_INACTIVE_HOVER_BG;
                    e.currentTarget.style.boxShadow = `inset 0 0 0 1px ${GRAY_INACTIVE_HOVER_BR}`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = GRAY_INACTIVE_BG;
                    e.currentTarget.style.boxShadow = `inset 0 0 0 1px ${GRAY_INACTIVE_BR}`;
                  }
                }}
              >
                <span className={isActive ? "font-semibold text-[#111]" : "text-black/70"}>
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div className="aspect-square mx-auto max-w-[220px] sm:max-w-[260px] md:max-w-[240px] lg:max-w-[280px]">
        <Doughnut data={chartData} options={options} />
      </div>

      {/* Legend */}
      <div className="mt-4 lg:mt-8 grid grid-cols-2 gap-y-2 gap-x-4 text-left sm:flex sm:flex-wrap sm:justify-center sm:gap-x-8 sm:gap-y-0">
        {labels.map((label, i) => (
          <div key={label} className="flex items-center gap-2 justify-start">
            <span
              className="inline-block w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: colors[i] }}
            />
            <span className="text-sm font-medium text-strobe-black">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
